/**
 * Tool Wrapper Component
 * 
 * Wraps prioritization tools with:
 * 1. "Import from Workspace" button to load shaped items
 * 2. "Save Results" flow to create Session Results
 * 3. Breadcrumb navigation back to tools index
 * 
 * The save flow:
 * 1. Tool produces results (rankings, scores, categories)
 * 2. User clicks "Save Results"
 * 3. Modal lets them save session results
 * 4. Results are saved as Session Results with item rankings
 */

import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CollaborativeSessionPanel } from './tools/CollaborativeSessionPanel';
import { getOutputType } from '../../lib/toolRegistry';
import type { OutputForm } from '../../lib/methodRegistry';
import { 
  ChevronLeft, 
  Download, 
  Upload, 
  CheckCircle, 
  X,
  Plus,
  ListChecks,
  Database,
  PencilLine,
  FileUp,
  ArrowLeft,
  MessageSquare,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { EntityCommentsTab } from './activity/EntityCommentsTab';
import { ToolItemsContext, ToolItemsProvider } from '../../contexts/ToolItemsContext';
import { WorkspaceSessionWizard } from './WorkspaceSessionWizard';
import {
  useToolItems,
  useShapedItemsForTools,
  useToolHostContext,
  useToolMode,
  type ToolItem,
  type ToolHostContext,
} from '../../contexts/toolItemsHooks';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { AppModeZeroState } from './AppModeZeroState';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { POOL_CREATE_LINE, SESSION_CREATE_LINE, useCreateRights } from '../../lib/authorization/createRights';

// Re-export AppModeZeroState for tools to use
export { AppModeZeroState } from './AppModeZeroState';
export type { AppModeZeroStateProps } from './AppModeZeroState';
export type { OutputForm } from '../../lib/methodRegistry';

// ============================================================================
// Output Form Context
// Allows any component inside a ToolWrapper to read the output form that
// the tool was configured with (set by the Session Launcher or by convention).
// SessionDetail reads this when routing completion results.
// ============================================================================

const ToolOutputFormContext = createContext<OutputForm | undefined>(undefined);

/**
 * Read the output_form that this tool is configured to produce.
 * Returns undefined when outside a ToolWrapper or when no outputForm was set.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useToolOutputForm(): OutputForm | undefined {
  return useContext(ToolOutputFormContext);
}

// Tool result item - what the tool produces
export interface ToolResultItem {
  id: string;           // Item ID (same as sourceId from import)
  name: string;
  rank?: number;        // 1-based rank (1 = highest priority)
  score?: number;       // Numeric score if applicable
  category?: string;    // Category like "ui", "in", "un", "nn" for Eisenhower
  scoreBreakdown?: Record<string, number>; // Numeric detail safe to persist with session results
  classificationBreakdown?: Record<string, string>; // Structured categorical detail safe to persist with session results
  metadata?: Record<string, unknown>; // Tool-specific data that may be rich/non-numeric
}

// Display modes for tools
export type ToolDisplayMode = 'page' | 'slideOver' | 'modal';

export interface ToolHostProps {
  displayMode?: ToolDisplayMode;
  hostContext?: ToolHostContext;
  onClose?: () => void;
  isOpen?: boolean;
  onCompleteResults?: (results: ToolResultItem[]) => void | Promise<void>;
  completeActionLabel?: string;
  importSourceLabel?: string;
  /** Custom back navigation for page mode — replaces the default "All Tools" link. */
  backTo?: { label: string; onClick?: () => void; to?: string };
}

interface ToolWrapperProps {
  children: ReactNode;
  toolName: string;
  toolDescription?: string;
  // Called when user wants to import items into the tool
  onImportItems?: (items: ToolItem[]) => void;
  // Called to get results when saving - tool provides its current state
  onGetResults?: () => ToolResultItem[];
  // Legacy: Called when tool is done (replaced by onGetResults for new flow)
  onSaveResults?: () => void;
  // Number of items currently in the tool (for zero state detection)
  itemCount?: number;
  // Whether to show the prominent zero state when itemCount is 0 (app mode only)
  showZeroState?: boolean;
  // Display mode: page (default), slideOver, or modal
  displayMode?: ToolDisplayMode;
  hostContext?: ToolHostContext;
  // Callback when slide-over or modal is closed
  onClose?: () => void;
  // Whether the slide-over/modal is open (controlled mode)
  isOpen?: boolean;
  // Optional session-style completion handler; when present, results go back to caller instead of list-save flow
  onCompleteResults?: (results: ToolResultItem[]) => void | Promise<void>;
  completeActionLabel?: string;
  // Override source label for import CTA (e.g. Session, Track)
  importSourceLabel?: string;
  /**
   * The output form this tool produces — from the method registry.
   * When provided, callers can use sessionResultRouter.routeToolResults() to route
   * results to the correct persistence target (session_results, item_pool, etc.).
   * Passed through to onCompleteResults context via the ToolResultsContext.
   */
  outputForm?: OutputForm;
  /**
   * Custom back-navigation for the page-mode header bar.
   * When provided, replaces the default "← All Tools" link with a button that
   * calls onClick (or navigates to `to`) with the given label.
   * Used by sessions to show "← Back to Session".
   */
  backTo?: { label: string; onClick?: () => void; to?: string };
}

// ─── Tool Discussion Drawer ────────────────────────────────────────────────────
// Collapsible panel at the bottom of the tool page for async threaded discussion.
// Entity ID is the tool slug (text), scoped per workspace.

function ToolDiscussionDrawer({
  toolSlug,
  toolName,
  workspaceId,
}: {
  toolSlug: string
  toolName: string
  workspaceId: string
}) {
  const [open, setOpen] = useState(false)
  // Use "workspace:toolSlug" as the entityId since tool slugs are unique within a workspace
  const entityId = `${workspaceId}:${toolSlug}`

  return (
    <div className="mt-6 border border-[var(--rule)] overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="text-sm font-medium text-[var(--text-sec)]">
            Discussion — {toolName}
          </span>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" />
        ) : (
          <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
        )}
      </button>

      {open && (
        <div className="p-4">
          <EntityCommentsTab
            entityType="tool"
            entityId={entityId}
            workspaceId={workspaceId}
          />
        </div>
      )}
    </div>
  )
}

// ─── Main content ─────────────────────────────────────────────────────────────

function ToolWrapperContent({
  children,
  toolName,
  toolDescription,
  onImportItems,
  onGetResults,
  onSaveResults,
  itemCount = -1,
  showZeroState = true,
  displayMode = 'page',
  backTo,
  hostContext,
  onClose,
  isOpen = true,
  onCompleteResults,
  completeActionLabel = 'Use Results In Session',
  importSourceLabel: _importSourceLabel,
}: ToolWrapperProps) {
  void _importSourceLabel;
  const toolItemsContext = useToolItems();
  const explicitHostContext = useToolHostContext();
  const toolMode = useToolMode();
  const isAppMode = toolMode === 'app';
  const navigate = useNavigate();
  const shapedItems = useShapedItemsForTools();
  const availableItems = toolItemsContext?.items?.length ? toolItemsContext.items : shapedItems;
  const resolvedHostContext = hostContext ?? explicitHostContext;
  // ── Situational awareness ─────────────────────────────────────────────────
  // situationalContext is richer than the coarse hostContext. When present it
  // drives all import/zero-state/completion UI decisions. Falls back to legacy
  // logic when absent so existing callers keep working unchanged.
  const situationalContext = toolItemsContext?.situationalContext;
  const inputMode =
    situationalContext?.inputMode ??
    (resolvedHostContext === 'standalone' ? 'manual' : 'workspace');

  // Show import/add button when the tool accepts items, we're not in preloaded mode,
  // and the zero state isn't already visible (zero state has its own three-path entry points)
  const showImportButton = inputMode !== 'preloaded' && !!onImportItems && itemCount !== 0;
  // Always "Add Items" — the modal makes all three paths visible
  const importButtonLabel = 'Add Items';

  // Helpers to open the modal pre-set to a specific path
  const openImportModal = (tab: 'choose' | 'workspace' | 'manual' | 'upload' = 'choose') => {
    setImportTab(tab);
    setShowImportModal(true);
  };

  // Show zero-state chrome only when the tool is in manual/workspace mode with no items loaded
  const showZeroStateEffective =
    inputMode !== 'preloaded' && isAppMode && showZeroState && itemCount === 0 && !!onImportItems;

  // Completion button label — comes from situational context or falls back to prop
  const effectiveCompleteLabel = situationalContext?.completion?.label ?? completeActionLabel;

  // Optional hint text shown beneath completion button (e.g. "→ Session results")
  const destinationHint = situationalContext?.completion?.destinationHint;

  // Collaborative mode: available when in a session with multiple participants
  const userId = useUserStore(state => state.userId);
  const sessionParticipants = situationalContext?.sessionParticipants ?? [];
  const currentUserId = situationalContext?.currentUserId ?? userId ?? 'unknown';
  const currentUserName = situationalContext?.currentUserName ?? 'You';
  const isFacilitator = situationalContext?.isFacilitator ?? false;
  const existingToolSessionId = situationalContext?.activeToolSessionId;
  const workspaceId = situationalContext?.workspaceId || getCurrentWorkspaceId() || '';
  const facilitatorId = situationalContext?.facilitatorId ?? '';
  const facilitatorName = situationalContext?.facilitatorName ?? '';
  const presentUsers = situationalContext?.presentUsers ?? [];

  // What the signed-in person may create here, as the insert rules decide
  // (build contract §2.8). Outside a session, "Configure Session" opens
  // WorkspaceSessionWizard, which creates a session (SESSION_RUN, migration
  // 754); inside a session or task it only picks items, so anyone may.
  const createRights = useCreateRights();
  const mayConfigureSession =
    resolvedHostContext === 'session' || resolvedHostContext === 'task' || createRights.session;
  // Save Session Result creates an item pool with no cycle or track
  // (item_pools insert, migration 754: POOL_SELECT). "Save items to platform"
  // in its modal creates items (items insert, migration 751: items.create).
  const maySaveResult = createRights.pool;
  const mayCreateItems = createRights.items;

  // Detect tool type from the current URL path (e.g. /tools/eisenhower-matrix → 'eisenhower-matrix')
  // This is used to configure the ToolSession output type
  const location = useLocation();
  const toolTypeFromPath = location.pathname.split('/tools/')[1]?.split('/')[0] ?? '';
  const toolOutputType = getOutputType(toolTypeFromPath);

  // Collaborative mode — must be declared before showCollaborativePanel references it
  const [collaborativeDismissed, setCollaborativeDismissed] = useState(false);

  // Show collaborative panel when: in a session, ≥2 participants, items loaded, not dismissed
  const showCollaborativePanel =
    resolvedHostContext === 'session' &&
    sessionParticipants.length >= 2 &&
    itemCount > 0 &&
    !collaborativeDismissed &&
    inputMode === 'preloaded';

  // ── Auto-inject preloaded items ───────────────────────────────────────────
  // When the tool is given items from a session/track/cycle, inject them
  // automatically on mount so the tool wakes up ready — no import step needed.
  const autoInjectedRef = useRef(false);
  useEffect(() => {
    if (
      inputMode === 'preloaded' &&
      !autoInjectedRef.current &&
      onImportItems &&
      availableItems.length > 0
    ) {
      autoInjectedRef.current = true;
      onImportItems(availableItems);
    }
  }, [inputMode, onImportItems, availableItems]);

  // Session Results store (legacy: itemPoolsStore)
  const createItemPool = useItemPoolsStore(state => state.createItemPool);
  const updateRanking = useItemPoolsStore(state => state.updateRanking);
  const addPriorityToList = useItemPoolsStore(state => state.addPriorityToList);
  
  // Candidates store for promoting items
  const promoteToPriority = useItemsStore(state => state.promoteToPriority);
  const createItem = useItemsStore(state => state.createItem);
  
  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [importState, setImportState] = useState<'idle' | 'success'>('idle');
  // Import modal tab state — 'choose' is the landing screen showing all three paths
  const [importTab, setImportTab] = useState<'choose' | 'workspace' | 'manual' | 'upload'>('choose');
  // Manual item entry text
  const [manualText, setManualText] = useState('');
  // Track whether any loaded items came from external entry (not the platform)
  const [hasExternalItems, setHasExternalItems] = useState(false);

  // Save modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveMode, setSaveMode] = useState<'new' | 'existing'>('new');
  const [newListName, setNewListName] = useState(`${toolName} Results`);
  const [newListDescription, setNewListDescription] = useState('');
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [, setSavedListId] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  // Save-to-platform checkbox in save modal
  const [saveExternalToPlatform, setSaveExternalToPlatform] = useState(false);
  // Session ID captured when items were loaded via WorkspaceSessionWizard ("Configure Session")
  const [wizardSessionId, setWizardSessionId] = useState<string | null>(null);
  
  // Parse manual text entry: one item per line, optional "title, description" format
  const parseManualItems = (text: string): { title: string; description: string }[] => {
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        const commaIdx = line.indexOf(',');
        if (commaIdx > 0) {
          return { title: line.slice(0, commaIdx).trim(), description: line.slice(commaIdx + 1).trim() };
        }
        return { title: line, description: '' };
      });
  };

  const handleImportManual = () => {
    if (!onImportItems) return;
    const parsed = parseManualItems(manualText);
    if (parsed.length === 0) return;
    const toolItems: ToolItem[] = parsed.map((item, i) => ({
      id: `ext-${Date.now()}-${i}`,
      name: item.title,
      description: item.description,
      sourceType: 'item' as const,
    }));
    onImportItems(toolItems);
    setHasExternalItems(true);
    setImportState('success');
    setTimeout(() => {
      setShowImportModal(false);
      setImportState('idle');
      setManualText('');
    }, 1500);
  };

  const downloadTemplate = () => {
    const csv = 'title,description\nItem One,Brief description of this item\nItem Two,Optional description\nItem Three,';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${toolName.toLowerCase().replace(/\s+/g, '-')}-import-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      if (!text) return;
      // Parse CSV: skip header row if it contains "title"
      const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
      const startIdx = lines[0]?.toLowerCase().startsWith('title') ? 1 : 0;
      const parsed = lines.slice(startIdx).map(line => {
        const parts = line.split(',');
        return { title: parts[0]?.trim() ?? '', description: parts[1]?.trim() ?? '' };
      }).filter(p => p.title.length > 0);
      setManualText(parsed.map(p => p.description ? `${p.title}, ${p.description}` : p.title).join('\n'));
      setImportTab('manual');
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    if (!onImportItems) return;

    const itemsToImport = availableItems.filter(item => selectedItems.has(item.id));
    onImportItems(itemsToImport);
    setImportState('success');
    
    setTimeout(() => {
      setShowImportModal(false);
      setImportState('idle');
    }, 1500);
  };
  
  const toggleItem = (id: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  
  const selectAll = () => {
    setSelectedItems(new Set(availableItems.map(item => item.id)));
  };
  
  const selectNone = () => {
    setSelectedItems(new Set());
  };
  
  // Handle opening save modal
  const handleOpenSaveModal = () => {
    // Reset state
    setSaveState('idle');
    setNewListName(`${toolName} Results - ${new Date().toLocaleDateString()}`);
    setNewListDescription(`Results from ${toolName} prioritization`);
    setSaveMode('new');
    setSelectedListId('');
    setShowSaveModal(true);
  };

  const handleComplete = async () => {
    if (!onCompleteResults || !onGetResults) return;
    setCompleting(true);
    setCompleteError(null);
    try {
      const results = onGetResults();
      if (results.length === 0) {
        throw new Error('Finish rating all items before submitting your response.');
      }
      await onCompleteResults(results);
    } catch (error) {
      console.error('Failed to complete tool results:', error);
      setCompleteError(error instanceof Error ? error.message : 'Failed to complete tool run.');
    } finally {
      setCompleting(false);
    }
  };
  
  // Handle saving results as session result
  const handleSaveToFocusList = async () => {
    if (!onGetResults) {
      // Legacy fallback
      if (onSaveResults) {
        onSaveResults();
      }
      return;
    }
    
    setSaveState('saving');
    
    try {
      const results = onGetResults();
      
      if (results.length === 0) {
        setSaveState('error');
        return;
      }
      
      // Sort by rank if available, otherwise by score
      const sortedResults = [...results].sort((a, b) => {
        if (a.rank !== undefined && b.rank !== undefined) {
          return a.rank - b.rank;
        }
        if (a.score !== undefined && b.score !== undefined) {
          return b.score - a.score; // Higher score = higher priority
        }
        return 0;
      });
      
      let listId: string;
      
      if (saveMode === 'new') {
        // Create new session result (async — must await so listId is the UUID string, not a Promise)
        listId = await createItemPool({
          name: newListName || `${toolName} Results`,
          description: newListDescription || undefined,
          ownerId: userId,
          cutLinePosition: sortedResults.length, // All items above line by default
          comparisonSetId: '',
          criteriaIds: [],
          status: 'draft',
          tags: [],
          // Cast for `trackId`: ItemPool types it required, but the column is
          // nullable and the store supports standalone pools — same cast the
          // other createItemPool callers carry.
        } as unknown as Parameters<typeof createItemPool>[0]);
      } else {
        listId = selectedListId;
      }
      
      // Add items to the list
      // First, promote any items that aren't yet priorities
      for (let i = 0; i < sortedResults.length; i++) {
        const result = sortedResults[i];
        const itemId = result.id;
        
        // Promote to priority (this handles if already a priority)
        promoteToPriority(itemId, listId, i + 1);
        
        // Add to session result — bypass the workspace readiness check since
        // tool results are explicitly user-ranked outputs, not items being
        // manually pulled from the workspace picker.
        addPriorityToList(listId, itemId, undefined, undefined, undefined, 'imported', { bypassReadinessCheck: true });
      }
      
      // Update the ranking order — exclude external (non-UUID) items that haven't been
      // persisted to the database yet; ext- IDs are invalid for Supabase uuid columns.
      const persistedIds = sortedResults.map(r => r.id).filter(id => !id.startsWith('ext-'));
      updateRanking(listId, persistedIds);
      
      setSavedListId(listId);
      setSaveState('success');

      // If user opted to save external items to the platform, create platform items
      if (saveExternalToPlatform && hasExternalItems && mayCreateItems) {
        // For external items (id starts with ext-), create platform items
        const externalToolItems = sortedResults.filter(r => r.id.startsWith('ext-'));
        for (const item of externalToolItems) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            createItem({ title: item.name, description: item.name, tags: [], createdBy: userId || 'user' } as any);
          } catch (e) {
            console.warn('Failed to create platform item for external entry:', item.name, e);
          }
        }
      }

      // Navigate to the session results page if items came via "Configure Session",
      // otherwise fall back to the legacy item pool list view.
      setTimeout(() => {
        setShowSaveModal(false);
        if (wizardSessionId) {
          navigate(`/prioritization/reports/session/${wizardSessionId}`);
        } else {
          navigate(`/prioritization/lists/${listId}`);
        }
      }, 2000);
      
    } catch (error) {
      console.error('Failed to save results:', error);
      setSaveState('error');
    }
  };
  
  // Render import modal (extracted for reuse in all display modes)
  const renderImportModal = () => {
    const parsedPreview = parseManualItems(manualText);
    const hasWorkspace = availableItems.length > 0;

    const closeModal = () => { setShowImportModal(false); setManualText(''); setImportTab('choose'); };

    // ── Choice screen ────────────────────────────────────────────────────────
    const renderChoiceScreen = () => (
      <div className="flex-1 px-6 py-6 space-y-5">
        {/* Configure Session — primary path */}
        {mayConfigureSession ? (
          <button
            onClick={() => setImportTab('workspace')}
            disabled={!hasWorkspace}
            className="group w-full flex items-center gap-4 border-2 border-[var(--rule)] bg-[var(--bg-raised)] px-5 py-4 hover:border-primary-400 hover:bg-primary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-left"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors shrink-0">
              <Database className="h-5 w-5 text-primary-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[var(--text-pri)] text-sm">Configure Session</p>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">
                {hasWorkspace
                  ? `Set up a workspace session — ${availableItems.length} item${availableItems.length !== 1 ? 's' : ''} available`
                  : 'Set up a workspace session (no items yet)'}
              </p>
            </div>
          </button>
        ) : (
          <p className="text-sm text-[var(--text-ter)]" data-testid="tool-configure-session-refused">
            {SESSION_CREATE_LINE}
          </p>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-[var(--hover-tint)]" />
          <span className="text-xs text-[var(--text-ter)] font-medium">or add items directly</span>
          <div className="flex-1 h-px bg-[var(--hover-tint)]" />
        </div>

        {/* Quick-add paths */}
        <div className="grid grid-cols-2 gap-4">
          {/* Type or Paste */}
          <button
            onClick={() => setImportTab('manual')}
            className="group flex flex-col items-center gap-3 border-2 border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-6 hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors text-center"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--slate-dim)] group-hover:bg-[var(--slate-dim)] transition-colors">
              <PencilLine className="h-5 w-5 text-[var(--text-ter)]" />
            </div>
            <div>
              <p className="font-semibold text-[var(--text-pri)] text-sm">Type or Paste</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">Enter items directly, one per line</p>
            </div>
          </button>

          {/* Upload CSV */}
          <button
            onClick={() => setImportTab('upload')}
            className="group flex flex-col items-center gap-3 border-2 border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-6 hover:border-[var(--green)] hover:bg-[var(--green-dim)] transition-colors text-center"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--green-dim)] group-hover:bg-[var(--green-dim)] transition-colors">
              <FileUp className="h-5 w-5 text-[var(--green)]" />
            </div>
            <div>
              <p className="font-semibold text-[var(--text-pri)] text-sm">Upload CSV</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">Import from a spreadsheet or template</p>
            </div>
          </button>
        </div>
      </div>
    );

    // ── Back button shared across sub-screens ─────────────────────────────────
    const renderBack = () => (
      <button
        onClick={() => setImportTab('choose')}
        className="inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
    );

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
        <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex flex-nowrap items-center justify-between gap-2 px-4 sm:px-6 py-4 border-b border-[var(--rule)]">
            <div className="flex items-center gap-3 min-w-0">
              {importTab !== 'choose' && renderBack()}
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-[var(--text-pri)]">
                  {importTab === 'choose' ? 'Add Items' :
                   importTab === 'workspace' ? 'Configure Session' :
                   importTab === 'manual' ? 'Type or Paste' : 'Upload CSV'}
                </h2>
                <p className="text-sm text-[var(--text-ter)]">
                  {importTab === 'choose'
                    ? `Choose how to bring items into ${toolName}`
                    : importTab === 'workspace'
                      ? `${availableItems.length} item${availableItems.length !== 1 ? 's' : ''} available from workspace`
                      : importTab === 'manual'
                        ? 'One item per line — optionally add a comma then a description'
                        : 'Upload a CSV file or download a blank template'}
                </p>
              </div>
            </div>
            <button onClick={closeModal} className="p-2 shrink-0 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          {importState === 'success' ? (
            <div className="flex-1 flex flex-col items-center justify-center py-12">
              <CheckCircle className="h-16 w-16 text-[var(--green)] mb-4" />
              <p className="text-lg font-semibold text-[var(--text-pri)]">
                {importTab === 'workspace' ? `${selectedItems.size} items imported!` : `${parsedPreview.length} items added!`}
              </p>
              <p className="text-sm text-[var(--text-ter)] mt-1">You can now work with them in {toolName}</p>
            </div>
          ) : importTab === 'choose' ? renderChoiceScreen() : (
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {importTab === 'workspace' && (
                // When NOT already inside a session, show the session-creation wizard
                // so "From Workspace" creates a proper session rather than importing into a
                // standalone tool run. Inside a session (hostContext === 'session' | 'task'),
                // keep the plain checklist so facilitators can add more items inline.
                resolvedHostContext === 'session' || resolvedHostContext === 'task' ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-[var(--text-ter)]">{availableItems.length} items</span>
                      <div className="flex gap-3">
                        <button onClick={selectAll} className="text-xs text-[var(--text-ter)] hover:underline">Select all</button>
                        <button onClick={selectNone} className="text-xs text-[var(--text-ter)] hover:underline">Clear</button>
                      </div>
                    </div>
                    {availableItems.map(item => (
                      <label key={item.id} className="flex items-start gap-3 p-3 border cursor-pointer hover:bg-[var(--surface)]">
                        <input type="checkbox" checked={selectedItems.has(item.id)} onChange={() => toggleItem(item.id)} className="mt-1 rounded" />
                        <div className="flex-1">
                          <div className="font-medium text-[var(--text-pri)] text-sm">{item.name}</div>
                          {item.description && <div className="text-xs text-[var(--text-ter)] mt-0.5 line-clamp-2">{item.description}</div>}
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <WorkspaceSessionWizard
                    toolName={toolName}
                    availableItems={availableItems}
                    onCancel={() => setImportTab('choose')}
                    onComplete={(sessionId, items) => {
                      setShowImportModal(false);
                      setImportTab('choose');
                      // Live + participant sessions navigate to /live; don't keep items in standalone tool.
                      const created = useSessionStore.getState().sessions.find(s => s.id === sessionId);
                      const goesToLive =
                        created?.modeConfig?.timingMode === 'live' &&
                        created?.modeConfig?.entryMode === 'participants';
                      if (!goesToLive) {
                        setWizardSessionId(sessionId);
                        if (onImportItems && items.length > 0) onImportItems(items);
                      }
                    }}
                  />
                )
              )}

              {importTab === 'manual' && (
                <div className="space-y-4">
                  <textarea
                    value={manualText}
                    onChange={e => setManualText(e.target.value)}
                    placeholder={"Enter items here, one per line...\n\nExample:\nItem one\nItem two, with a description\nItem three"}
                    className="w-full h-52 px-3 py-2 border border-[var(--rule)] text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    autoFocus
                  />
                  {parsedPreview.length > 0 && (
                    <div className="bg-[var(--surface)] border p-3">
                      <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider mb-2">{parsedPreview.length} items detected</p>
                      <div className="space-y-1">
                        {parsedPreview.slice(0, 5).map((item, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <span className="text-[var(--text-ter)] text-xs w-4">{i + 1}.</span>
                            <span className="font-medium text-[var(--text-pri)]">{item.title}</span>
                            {item.description && <span className="text-[var(--text-ter)] text-xs">— {item.description}</span>}
                          </div>
                        ))}
                        {parsedPreview.length > 5 && (
                          <p className="text-xs text-[var(--text-ter)] pl-6">+{parsedPreview.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {importTab === 'upload' && (
                <div className="space-y-6">
                  <label className="flex flex-col items-center justify-center gap-4 border-2 border-dashed border-[var(--rule)] bg-[var(--surface)] px-6 py-12 text-center cursor-pointer hover:border-[var(--green)] hover:bg-[var(--green-dim)] transition-colors">
                    <FileUp className="h-10 w-10 text-[var(--green)]" />
                    <div>
                      <p className="font-semibold text-[var(--text-pri)]">Click to upload a CSV or TXT file</p>
                      <p className="text-sm text-[var(--text-ter)] mt-1">
                        First column = item name, second column = description (optional)
                      </p>
                    </div>
                    <input type="file" accept=".csv,.txt" className="sr-only" onChange={e => { handleFileUpload(e); setImportTab('manual'); }} />
                  </label>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-[var(--hover-tint)]" />
                    <span className="text-xs text-[var(--text-ter)]">or</span>
                    <div className="flex-1 h-px bg-[var(--hover-tint)]" />
                  </div>

                  <div className="border border-[var(--rule)] p-4">
                    <p className="text-sm font-medium text-[var(--text-pri)] mb-1">Download blank template</p>
                    <p className="text-xs text-[var(--text-ter)] mb-3">
                      Fill it in with your items and upload it above.
                    </p>
                    <button
                      onClick={downloadTemplate}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)] hover:bg-[var(--green-dim)] transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      Download template (.csv)
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer — hidden when the wizard is showing (it has its own footer) */}
          {importState !== 'success' && importTab !== 'choose' && importTab !== 'upload' &&
           !(importTab === 'workspace' && resolvedHostContext !== 'session' && resolvedHostContext !== 'task') && (
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
              <button onClick={closeModal} className="px-4 py-2 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)]">
                Cancel
              </button>
              {importTab === 'workspace' ? (
                <button
                  onClick={handleImport}
                  disabled={selectedItems.size === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] disabled:opacity-50 hover:bg-[var(--slate)]"
                >
                  Import {selectedItems.size > 0 ? `${selectedItems.size} Items` : 'Items'}
                </button>
              ) : (
                <button
                  onClick={handleImportManual}
                  disabled={parsedPreview.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] disabled:opacity-50 hover:bg-[var(--slate)]"
                >
                  Add {parsedPreview.length > 0 ? `${parsedPreview.length} Items` : 'Items'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render save modal (extracted for reuse)
  const renderSaveModal = () => {
    const currentResults = onGetResults ? onGetResults() : [];
    const sortedPreview = [...currentResults]
      .sort((a, b) => {
        if (a.rank !== undefined && b.rank !== undefined) return a.rank - b.rank;
        if (a.score !== undefined && b.score !== undefined) return b.score - a.score;
        return 0;
      })
      .slice(0, 6);

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
        <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg mx-4">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Save Results</h2>
            <button onClick={() => setShowSaveModal(false)} className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-6 py-4">
            {saveState === 'success' ? (
              <div className="flex flex-col items-center py-8">
                <CheckCircle className="h-16 w-16 text-[var(--green)] mb-4" />
                <p className="text-lg font-semibold text-[var(--text-pri)]">Saved!</p>
                <p className="text-sm text-[var(--text-ter)] mt-1">Taking you to your results…</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Results preview */}
                {sortedPreview.length > 0 && (
                  <div className="bg-[var(--surface)] border p-3">
                    <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">
                      {currentResults.length} items · Ranked result preview
                    </p>
                    <div className="space-y-1.5">
                      {sortedPreview.map((item, i) => (
                        <div key={item.id} className="flex items-center gap-2 text-sm">
                          <span className="w-5 h-5 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="font-medium text-[var(--text-pri)] truncate">{item.name}</span>
                          {item.category && (
                            <span className="ml-auto text-xs text-[var(--text-ter)] flex-shrink-0">{item.category.toUpperCase()}</span>
                          )}
                          {item.score !== undefined && !item.category && (
                            <span className="ml-auto text-xs text-[var(--text-ter)] flex-shrink-0">score: {item.score.toFixed(1)}</span>
                          )}
                        </div>
                      ))}
                      {currentResults.length > 6 && (
                        <p className="text-xs text-[var(--text-ter)] pl-7">+{currentResults.length - 6} more</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Name input */}
                <div>
                  <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Result Set Name</label>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="Name your result set"
                    className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Save external items to platform — creates items (items.create) */}
                {hasExternalItems && mayCreateItems && (
                  <label className="flex items-start gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveExternalToPlatform}
                      onChange={(e) => setSaveExternalToPlatform(e.target.checked)}
                      className="mt-0.5 rounded"
                    />
                    <div>
                      <p className="text-sm font-medium text-[var(--mustard)]">Save items to platform</p>
                      <p className="text-xs text-[var(--mustard)] mt-0.5">
                        {
                          "Creates platform items from your entered items so they're reusable in future sessions and cycles."
                        }
                      </p>
                    </div>
                  </label>
                )}

                {saveState === 'error' && (
                  <p className="text-sm text-[var(--coral)]">Something went wrong. Please try again.</p>
                )}
              </div>
            )}
          </div>
          {saveState !== 'success' && (
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
              <button onClick={() => setShowSaveModal(false)} className="px-4 py-2 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)]">
                Cancel
              </button>
              <button
                onClick={handleSaveToFocusList}
                disabled={saveState === 'saving' || !newListName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[var(--green)] disabled:opacity-50 hover:bg-[var(--green)]"
              >
                {saveState === 'saving' ? 'Saving…' : 'Save Results'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // SlideOver wrapper for panel mode
  if (displayMode === 'slideOver') {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-[var(--surface)] bg-opacity-75 transition-opacity"
            onClick={onClose}
          />
          
          {/* Panel */}
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <div className="pointer-events-auto w-screen max-w-2xl">
              <div className="flex h-full flex-col overflow-y-auto bg-[var(--bg-raised)] shadow-xl">
                {/* Panel Header */}
                <div className="flex items-center justify-between px-4 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--text-pri)]">{toolName}</h2>
                    {toolDescription && (
                      <p className="mt-1 text-sm text-[var(--text-ter)]">{toolDescription}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {showImportButton && (
                      <button
                        onClick={() => openImportModal()}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        {importButtonLabel}
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="text-[var(--text-ter)] hover:text-[var(--text-ter)] focus:outline-none"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Panel Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  {showCollaborativePanel && situationalContext?.sessionId && (
                    <CollaborativeSessionPanel
                      toolType={toolTypeFromPath}
                      toolName={toolName}
                      outputType={toolOutputType}
                      sessionId={situationalContext.sessionId}
                      workspaceId={workspaceId}
                      participants={sessionParticipants}
                      currentUserId={currentUserId}
                      currentUserName={currentUserName}
                      isFacilitator={isFacilitator}
                      facilitatorId={facilitatorId}
                      facilitatorName={facilitatorName}
                      toolItems={availableItems.map(i => ({ id: i.id, title: i.name, description: i.description }))}
                      onRunSolo={() => setCollaborativeDismissed(true)}
                      onGetResults={onGetResults ?? (() => [])}
                      onFinalResults={(results) => {
                        if (onCompleteResults) {
                          onCompleteResults(results);
                        }
                      }}
                      existingToolSessionId={existingToolSessionId}
                      presentUsers={presentUsers}
                    />
                  )}
                  {children}
                </div>
                
                {/* Panel Footer with save action */}
                {onGetResults && (
                  <div className="border-t border-[var(--rule)] px-4 py-3 bg-[var(--surface)]">
                    <div className="flex items-center justify-end gap-3">
                      {destinationHint && (
                        <span className="text-xs text-[var(--text-ter)] mr-auto">{destinationHint}</span>
                      )}
                      <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
                      >
                        Cancel
                      </button>
                      {onCompleteResults ? (
                        <button
                          onClick={handleComplete}
                          disabled={completing}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                        >
                          <ListChecks className="h-4 w-4" />
                          {completing ? 'Saving…' : effectiveCompleteLabel}
                        </button>
                      ) : maySaveResult ? (
                        <button
                          onClick={handleOpenSaveModal}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[var(--green)] hover:bg-[var(--green)]"
                        >
                          <ListChecks className="h-4 w-4" />
                          Save Session Result
                        </button>
                      ) : (
                        <p className="text-xs text-[var(--text-ter)]" data-testid="tool-save-result-refused">{POOL_CREATE_LINE}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Import and Save modals (same as page mode) */}
        {showImportModal && renderImportModal()}
        {showSaveModal && renderSaveModal()}
      </div>
    );
  }
  
  // Modal wrapper for modal mode
  if (displayMode === 'modal') {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-[var(--surface)] bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative w-full max-w-4xl max-h-[90vh] mx-4 bg-[var(--bg-raised)] shadow-xl overflow-hidden flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-pri)]">{toolName}</h2>
              {toolDescription && (
                <p className="mt-1 text-sm text-[var(--text-ter)]">{toolDescription}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {showImportButton && (
                <button
                  onClick={() => openImportModal()}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  {importButtonLabel}
                </button>
              )}
              <button
                onClick={onClose}
                className="text-[var(--text-ter)] hover:text-[var(--text-ter)] focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {children}
          </div>
          
          {/* Modal Footer */}
          {onGetResults && (
            <div className="border-t border-[var(--rule)] px-6 py-4">
              <div className="flex items-center justify-end gap-3">
                {destinationHint && (
                  <span className="text-xs text-[var(--text-ter)] mr-auto">{destinationHint}</span>
                )}
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
                >
                  Cancel
                </button>
                {onCompleteResults ? (
                  <button
                    onClick={handleComplete}
                    disabled={completing}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    <ListChecks className="h-4 w-4" />
                    {completing ? 'Saving…' : effectiveCompleteLabel}
                  </button>
                ) : maySaveResult ? (
                  <button
                    onClick={handleOpenSaveModal}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[var(--green)] hover:bg-[var(--green)]"
                  >
                    <ListChecks className="h-4 w-4" />
                    Save Session Result
                  </button>
                ) : (
                  <p className="text-xs text-[var(--text-ter)]" data-testid="tool-save-result-refused">{POOL_CREATE_LINE}</p>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Import and Save modals */}
        {showImportModal && renderImportModal()}
        {showSaveModal && renderSaveModal()}
      </div>
    );
  }
  
  // Live session overlay (LiveToolRunner): no full-page chrome — shell already has nav + top bar
  const isLiveEmbedded =
    resolvedHostContext === 'session'
    && displayMode === 'page'
    && inputMode === 'preloaded'
    && situationalContext?.completion?.navigateOnComplete === false;

  if (isLiveEmbedded) {
    return (
      <div className="w-full min-w-0 max-w-full h-full flex flex-col overflow-hidden bg-[var(--bg)] text-[var(--text-pri)]">
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-5">
          {completeError && (
            <div className="mb-4 border border-luminous-coral-700/35 bg-luminous-coral-700/10 px-4 py-3 text-sm text-luminous-coral-700 font-body">
              {completeError}
            </div>
          )}
          {showZeroStateEffective ? (
            <AppModeZeroState
              toolName={toolName}
              shapedItemCount={availableItems.length}
              onImportClick={mayConfigureSession ? () => openImportModal('workspace') : undefined}
              importRefusedLine={SESSION_CREATE_LINE}
              onManualClick={() => openImportModal('manual')}
              onUploadClick={() => openImportModal('upload')}
            />
          ) : (
            children
          )}
        </div>
        {onGetResults && onCompleteResults && (
          <div className="flex-shrink-0 border-t border-[var(--rule)] bg-[var(--bg-raised)] px-5 sm:px-6 py-3">
            <div className="flex items-center justify-end gap-3">
              {destinationHint && (
                <span className="text-xs text-[var(--text-ter)] mr-auto font-body">{destinationHint}</span>
              )}
              <button
                type="button"
                onClick={handleComplete}
                disabled={completing}
                className="inline-flex items-center gap-2 h-9 px-4 font-heading text-[0.62rem] tracking-[0.1em] uppercase text-alpine-green-400 border border-alpine-green-500/45 bg-alpine-green-500/15 hover:bg-alpine-green-500/30 disabled:opacity-50 transition-colors"
              >
                <ListChecks className="h-3.5 w-3.5" />
                {completing ? 'Saving…' : effectiveCompleteLabel}
              </button>
            </div>
          </div>
        )}
        {showImportModal && renderImportModal()}
        {showSaveModal && renderSaveModal()}
      </div>
    );
  }

  // Default page mode
  return (
    <div className="min-h-screen bg-[var(--surface)]">
      {/* Header Bar */}
      <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)] sticky top-0 z-40">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between min-h-14 py-2 gap-y-2">
            {/* Left: Navigation */}
            <div className="flex items-center gap-4">
              {backTo ? (
                backTo.onClick ? (
                  <button
                    onClick={backTo.onClick}
                    className="flex items-center gap-1 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>{backTo.label}</span>
                  </button>
                ) : (
                  <Link
                    to={backTo.to ?? '/prioritization/tools'}
                    className="flex items-center gap-1 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>{backTo.label}</span>
                  </Link>
                )
              ) : (
                <Link
                  to="/prioritization/tools"
                  className="flex items-center gap-1 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span>All Tools</span>
                </Link>
              )}
              <div className="h-5 w-px bg-[var(--rule-bright)]" />
              <h1 className="text-lg font-semibold text-[var(--text-pri)]">{toolName}</h1>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {destinationHint && (
                <span className="text-xs text-[var(--text-ter)]">{destinationHint}</span>
              )}

              {showImportButton && (
                <button
                  onClick={() => openImportModal()}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  {importButtonLabel}
                </button>
              )}

              {onGetResults && onCompleteResults && (
                <>
                  {onClose && (
                    <button
                      onClick={onClose}
                      className="px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={handleComplete}
                    disabled={completing}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    <ListChecks className="h-4 w-4" />
                    {completing ? 'Saving…' : effectiveCompleteLabel}
                  </button>
                </>
              )}
              
              {onGetResults && !onCompleteResults && (maySaveResult ? (
                <button
                  onClick={handleOpenSaveModal}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[var(--green)] hover:bg-[var(--green)] transition-colors"
                >
                  <ListChecks className="h-4 w-4" />
                  Save Session Result
                </button>
              ) : (
                <p className="text-xs text-[var(--text-ter)]" data-testid="tool-save-result-refused">{POOL_CREATE_LINE}</p>
              ))}
              
              {/* Legacy save button */}
              {!onGetResults && onSaveResults && (
                <button
                  onClick={onSaveResults}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  Save Results
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Tool Content */}
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {completeError && (
          <div className="mb-4 border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-3 text-sm text-[var(--coral)]">
            {completeError}
          </div>
        )}
        {/* Zero state: only when items aren't pre-loaded and none have been added yet */}
        {showZeroStateEffective ? (
          <AppModeZeroState
            toolName={toolName}
            shapedItemCount={availableItems.length}
            onImportClick={() => openImportModal('workspace')}
            onManualClick={() => openImportModal('manual')}
            onUploadClick={() => openImportModal('upload')}
          />
        ) : (
          <>
            {/* Collaborative session panel — shown when in session with multiple participants */}
            {showCollaborativePanel && situationalContext?.sessionId && (
              <CollaborativeSessionPanel
                toolType={toolTypeFromPath}
                toolName={toolName}
                outputType={toolOutputType}
                sessionId={situationalContext.sessionId}
                workspaceId={workspaceId}
                participants={sessionParticipants}
                currentUserId={currentUserId}
                currentUserName={currentUserName}
                isFacilitator={isFacilitator}
                facilitatorId={facilitatorId}
                facilitatorName={facilitatorName}
                toolItems={availableItems.map(i => ({ id: i.id, title: i.name, description: i.description }))}
                onRunSolo={() => setCollaborativeDismissed(true)}
                onGetResults={onGetResults ?? (() => [])}
                onFinalResults={(results) => {
                  if (onCompleteResults) {
                    onCompleteResults(results);
                  }
                }}
                existingToolSessionId={existingToolSessionId}
                presentUsers={presentUsers}
              />
            )}
            {children}
          </>
        )}
        {/* Standalone zero state — mirror the same three-path layout */}
        {!isAppMode && itemCount === 0 && !!onImportItems && inputMode !== 'preloaded' && (
          <AppModeZeroState
            toolName={toolName}
            shapedItemCount={availableItems.length}
            onImportClick={() => openImportModal('workspace')}
            onManualClick={() => openImportModal('manual')}
            onUploadClick={() => openImportModal('upload')}
          />
        )}
      </div>
      
      {/* Import Modal */}
      {showImportModal && renderImportModal()}

      {/* Save Session Result Modal */}
      {showSaveModal && renderSaveModal()}

      {/* Tool Discussion Drawer — async threaded discussion scoped to this tool type */}
      {toolTypeFromPath && workspaceId && (
        <ToolDiscussionDrawer
          toolSlug={toolTypeFromPath}
          toolName={toolName}
          workspaceId={workspaceId}
        />
      )}
    </div>
  );
}

export default function ToolWrapper(props: ToolWrapperProps) {
  const existingContext = useContext(ToolItemsContext);

  const content = existingContext ? (
    <ToolWrapperContent {...props} />
  ) : (
    <ToolItemsProvider hostContext={props.hostContext}>
      <ToolWrapperContent {...props} />
    </ToolItemsProvider>
  );

  return (
    <ToolOutputFormContext.Provider value={props.outputForm}>
      {content}
    </ToolOutputFormContext.Provider>
  );
}
